/**
 * OAuthConsentModal — Xavior-side interstitial shown BEFORE we open
 * Google's consent popup, so users know exactly what we'll do with the
 * connection before they're handed off to a third-party screen.
 *
 * Triggered by the in-chat "Connect Gmail" / "Connect Calendar" /
 * "Connect Drive" chips (see ChatBox.tsx oauth.* kind handler).
 * #2703 follow-up: surfaces the read-only / no-send guarantee that
 * was previously buried in the chat prose.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";

export type OAuthConsentProvider =
  | "google.gmail"
  | "google.calendar"
  | "google.drive"
  | "google.full"
  | "microsoft.full";

interface ProviderCopy {
  title: string;
  /** Optional intro paragraph rendered above the bullet section. */
  intro?: string;
  /** Optional label rendered above the bullets (e.g., "Our promise to you"). */
  sectionLabel?: string;
  bullets: string[];
  /** Optional muted note rendered below the bullets. */
  footerNote?: string;
  /** Per-variant CTA label. Defaults to "Continue to Google" for read-only variants. */
  ctaLabel?: string;
}

const PROVIDER_COPY: Record<OAuthConsentProvider, ProviderCopy> = {
  "google.gmail": {
    title: "Connect Gmail",
    intro:
      "Xavior will read your inbox so it can triage messages, categorize threads, and surface what actually matters.",
    bullets: [
      "Xavior only sends, replies, and moves emails with your explicit approval.",
      "Your authorization is stored encrypted on your account; you can disconnect anytime in Settings → Integrations.",
      "You'll grant access on Google's consent screen next.",
    ],
  },
  "google.calendar": {
    title: "Connect Google Calendar",
    intro:
      "Xavior will read your calendar so it can surface upcoming events, find free time, and prep you for what's next.",
    bullets: [
      "Read-only access — Xavior never creates, modifies, or cancels events without your explicit approval.",
      "Your authorization is stored encrypted on your account; you can disconnect anytime in Settings → Integrations.",
      "You'll grant access on Google's consent screen next.",
    ],
  },
  "google.drive": {
    title: "Connect Google Drive",
    intro:
      "Xavior works with the Google Drive files you choose. Pick documents to bring in, and Xavior can summarize them and pull them into conversations.",
    bullets: [
      "Xavior only sees files you explicitly choose or that Xavior creates for you — never your whole Drive.",
      "Backups and docs Xavior creates stay in your Drive under a folder Xavior manages.",
      "You can disconnect anytime in Settings → Integrations.",
    ],
  },
  "google.full": {
    title: "Connect your Google Account",
    sectionLabel: "Our promise to you",
    bullets: [
      "Xavior never sends mail from your account on its own",
      "We never train on your data",
      "Disconnect anytime — every token and cached file wiped",
    ],
    footerNote: "One sign-in covers Gmail, Calendar, and Drive.",
    ctaLabel: "Connect Google",
  },
  "microsoft.full": {
    title: "Connect Microsoft 365",
    sectionLabel: "Our promise to you",
    bullets: [
      "Xavior never sends mail from your account on its own",
      "We never train on your data",
      "Disconnect anytime — every token and cached file wiped",
    ],
    footerNote: "One sign-in covers Outlook, Calendar, OneDrive, and OneNote.",
    ctaLabel: "Connect Microsoft",
  },
};

interface OAuthConsentModalProps {
  open: boolean;
  provider: OAuthConsentProvider | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function OAuthConsentModal({
  open,
  provider,
  onConfirm,
  onCancel,
}: OAuthConsentModalProps) {
  // Close on Escape so keyboard users have an obvious dismiss path.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  if (!open || !provider) return null;
  const copy = PROVIDER_COPY[provider];

  // #2750: backdrop must overlay the sidebar/rail too.
  // Stack reality (search apps/web/src for `2147483647`):
  //   - Several modules use MAX_INT (2147483647) for "top of stack" modals
  //     (TaskEditModal, ContactEditModal, EventEditModal, FileOverlay,
  //     MainLayout, etc.).
  //   - ContactEditModal's lower layer uses MAX_INT - 1
  //     (2147483646) when it needs to sit just BELOW one of those modals.
  // OAuthConsent intentionally targets MAX_INT - 1 so it loses cleanly to
  // any in-flight TaskEdit/ContactEdit/EventEdit modal — the user shouldn't
  // see the consent ask buried beneath a half-filled task edit, but if one
  // of those modals is already on screen we want the user to finish (or
  // explicitly close) it before consenting. The inline zIndex avoids
  // Tailwind purge edge cases on arbitrary `z-[NNNNN]` classes. See
  // review H8.
  return createPortal(
    <div
      style={{ zIndex: 2147483646 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="oauth-consent-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-3xl border border-border bg-surface p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="oauth-consent-title"
          className="text-lg font-semibold text-text"
        >
          {copy.title}
        </h2>
        {copy.intro && (
          <p className="mt-3 text-sm leading-relaxed text-text-secondary">
            {copy.intro}
          </p>
        )}
        {copy.sectionLabel && (
          <p className="mt-4 text-xs font-medium uppercase tracking-wide text-text-muted">
            {copy.sectionLabel}
          </p>
        )}
        <ul className="mt-3 space-y-2 text-sm text-text-secondary">
          {copy.bullets.map((b) => (
            <li key={b} className="flex gap-2">
              <span className="mt-0.5 flex-shrink-0 text-green-600">✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {copy.footerNote && (
          <p className="mt-4 text-sm leading-relaxed text-text-secondary">
            {copy.footerNote}
          </p>
        )}
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium text-text transition hover:border-text-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-lg bg-sky-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-sky-600"
          >
            {copy.ctaLabel ?? "Continue"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Resolve an OAuthConsentProvider from a chip `kind` string of the form
 * `"oauth.<provider>.<scope>"`. Returns null for kinds we don't have copy for.
 */
export function resolveConsentProvider(
  chipKind: string,
): OAuthConsentProvider | null {
  if (!chipKind.startsWith("oauth.")) return null;
  const slug = chipKind.slice("oauth.".length);
  if (
    slug === "google.gmail" ||
    slug === "google.calendar" ||
    slug === "google.drive"
  ) {
    return slug;
  }
  if (slug.startsWith("microsoft.")) {
    return "microsoft.full";
  }
  return null;
}
